<template>
  <d-modal
    :modal="modal"
    :title="
      editMode ? $t('users.edit.page-heading') : $t('users.add.page-heading')
    "
    @submitModal="handleSubmitModal"
    @closeModal="handleCloseModal"
    :isSubmitting="isSubmitting"
  >
    <template slot="body">
      <d-loading class="mb-4" v-if="loading.page"></d-loading>
      <v-row class="mb-1" v-else>
        <v-col cols="12" md="6" class="mb-3">
          <v-text-field
            :label="$t('users.add.form.username')"
            hide-details="auto"
            filled
            type="text"
            v-model="form.username"
            :rules="rule.username"
            :disabled="editMode"
            :error="errorMessages.username.length > 0"
            :error-messages="errorMessages.username"
            @blur="checkUsernameIsValid"
            :loading="isCheckingUsername"
          ></v-text-field>
        </v-col>

        <v-col cols="12" md="6" class="mb-3">
          <v-autocomplete
            filled
            hide-details="auto"
            :items="userTypes"
            item-text="displayText"
            item-value="value"
            clearable
            :label="$t('users.add.form.user-type')"
            v-model="form.userTypeCode"
            :rules="rule.required"
          ></v-autocomplete>
        </v-col>

        <v-col cols="12" class="mb-3">
          <v-text-field
            :label="$t('users.add.form.organization')"
            hide-details="auto"
            filled
            type="text"
            v-model="form.organizationName"
            :rules="rule.required"
          ></v-text-field>
        </v-col>

        <v-col cols="12" md="6" class="mb-3">
          <v-text-field
            :label="$t('users.add.form.password')"
            hide-details="auto"
            filled
            v-model.trim="form.password"
            :rules="rule.password"
            :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
            :type="showPassword ? 'text' : 'password'"
            @click:append="showPassword = !showPassword"
          ></v-text-field>
        </v-col>
        <v-col cols="12" md="6" class="mb-3">
          <v-text-field
            :label="$t('users.add.form.confirm-password')"
            hide-details="auto"
            filled
            v-model="form.confirmPassword"
            :rules="rule.confirmPassword"
            :append-icon="showConfirmPassword ? 'mdi-eye' : 'mdi-eye-off'"
            :type="showConfirmPassword ? 'text' : 'password'"
            @click:append="showConfirmPassword = !showConfirmPassword"
          ></v-text-field>
        </v-col>
      </v-row>
    </template>
  </d-modal>
</template>
  
  <script>
import {
  checkUsernameAvailability,
  addUser,
  getUserById,
  updateUser,
} from "@/services/user";

const initialForm = {
  id: null,
  username: null,
  userTypeCode: null,
  password: null,
  confirmPassword: null,
};

const initialErrorMessages = {
  username: [],
};

export default {
  props: {
    modal: {
      type: Boolean,
      default: false,
    },
    item: Object,
  },
  data() {
    return {
      isSubmitting: false,
      form: { ...initialForm },
      rule: {
        organizationName: [(v) => !!v || this.$t("defaults.rules.required")],
        username: [
          (v) => !!v || this.$t("defaults.rules.required"),
          (v) =>
            /^[a-zA-Z0-9._+-]{5,25}$/.test(v) ||
            this.$t("users.add.rules.username-invalid"),
        ],
        password: [
          (v) => {
            if (this.editMode && !v) {
              return true;
            } else if (!this.editMode && !v)
              return this.$t("defaults.rules.required");
            else if (
              !/^(?=.*[A-Za-z])(?=.*\d)(?=.*[^A-Za-z\d]).{8,}$/.test(v)
            ) {
              return this.$t("users.add.rules.password-invalid");
            }

            return true;
          },
        ],
        confirmPassword: [
          (v) => {
            if (!this.form.password) return true;

            if (v === this.form.password) return true;

            return this.$t("users.add.rules.confirm-password-not-match");
          },
        ],
      },
      userTypes: [],
      errorMessages: { ...initialErrorMessages },
      isCheckingUsername: false,
      loading: {
        page: false,
        departments: false,
        branches: false,
      },
      showPassword: false,
      showConfirmPassword: false,
    };
  },
  computed: {
    editMode() {
      return !!this.item?.id;
    },
  },
  methods: {
    async handleSubmitModal() {
      if (this.editMode) {
        await this.submitUpdateUser();
        return;
      }
      await this.submitAddUser();
    },
    async submitAddUser() {
      try {
        this.isSubmitting = true;
        await addUser(this.form);

        this.$eventBus.$emit("notification", {
          type: "success",
          message: this.$t("users.add.alert.added"),
        });
        this.$emit("submitModal");
      } catch {
        this.$eventBus.$emit("notification", {
          type: "error",
          message: this.$t("defaults.text.request-error"),
        });
      } finally {
        this.isSubmitting = false;
      }
    },
    async submitUpdateUser() {
      try {
        this.isSubmitting = true;
        await updateUser(this.form);

        this.$eventBus.$emit("notification", {
          type: "success",
          message: this.$t("users.edit.alert.updated"),
        });
        this.$emit("submitModal");
      } catch {
        this.$eventBus.$emit("notification", {
          type: "error",
          message: this.$t("defaults.text.request-error"),
        });
      } finally {
        this.isSubmitting = false;
      }
    },
    handleCloseModal() {
      this.$emit("closeModal");
    },
    async checkUsernameIsValid() {
      const { username } = this.form;
      if ((username && this.editMode) || !username) return;

      if (/^[a-zA-Z0-9._+-]{5,25}$/.test(username)) {
        try {
          this.isCheckingUsername = true;
          const response = await checkUsernameAvailability(username);
          if (!response) {
            this.errorMessages.username = [
              this.$t("users.add.rules.username-is-taken"),
            ];
            return;
          }
          this.errorMessages.username = [];
        } finally {
          this.isCheckingUsername = false;
        }
      }
    },
    async getUserById() {
      try {
        const { data, items } = await getUserById(this.item?.id || -1);
        this.userTypes = items.userTypes;

        this.form = {
          ...data,
          userTypeCode: data.userType?.value || initialForm.userTypeCode,
          password: initialForm.password,
        };
      } catch {
        this.$eventBus.$emit("notification", {
          type: "error",
          message: this.$t("defaults.text.request-error"),
        });
      }
    },
  },
  async mounted() {
    try {
      this.loading.page = true;
      await this.getUserById();
    } finally {
      this.loading.page = false;
    }
  },
};
</script>
  